import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/shows';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribeStatus('loading');

    const formEl = e.currentTarget as HTMLFormElement;
    const formData = new FormData(formEl);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubscribeStatus('success');
      setSubscribeMsg('订阅成功！感谢您的关注。');
      setEmail('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9obkq4it35ii3jdm5b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('订阅成功！感谢您的关注。');
        setEmail('');
      } else {
        const serverMsg = (parsed && ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message) || '';
        setSubscribeStatus('error');
        setSubscribeMsg(serverMsg ? String(serverMsg) : '订阅失败，请稍后重试。');
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请检查您的网络连接后重试。');
    }
  };

  return (
    <footer className="bg-background-950 text-foreground-200">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-heading font-bold text-primary-400">
                6789<span className="text-background-50">影院</span>
              </span>
            </Link>
            <p className="text-sm text-foreground-400 leading-relaxed">
              6789影院收录全网热门综艺节目，免费在线观看高清流畅不缓冲。综艺节目大全每日更新，爆笑综艺、音乐选秀、真人秀全收录。
            </p>
          </div>

          <div>
            <h4 className="text-sm font-heading font-semibold text-background-50 mb-4">综艺分类</h4>
            <ul className="space-y-2">
              {categories.filter(c => c.slug !== 'all').map(cat => (
                <li key={cat.slug}>
                  <Link to={`/category/${cat.slug}`} className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-heading font-semibold text-background-50 mb-4">快速链接</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">首页</Link></li>
              <li><Link to="/about" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">关于我们</Link></li>
              <li><a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">常见问题</a></li>
              <li><a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">使用条款</a></li>
              <li><a href="#" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200">隐私政策</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-heading font-semibold text-background-50 mb-4">订阅更新</h4>
            <p className="text-sm text-foreground-400 mb-4 leading-relaxed">订阅我们的 newsletter，第一时间获取最新综艺更新通知。</p>
            <form onSubmit={handleSubscribe} className="space-y-2" data-readdy-form>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入您的邮箱"
                required
                className="w-full h-10 px-4 rounded-lg bg-background-800 border border-background-700 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="anti-spam-field"
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'loading'}
                className="w-full h-10 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {subscribeStatus === 'loading' ? '提交中...' : '立即订阅'}
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-sm text-green-400 mt-2">{subscribeMsg}</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-sm text-red-400 mt-2">{subscribeMsg}</p>
            )}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-foreground-500">
              &copy; {new Date().getFullYear()} 6789影院 版权所有 | 热门综艺节目免费在线观看
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="B站">
                <i className="ri-bilibili-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}